import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  createRestaurantAvatarProps,
  Icons,
  Surface,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import {
  createColumnHelper,
  flexRender,
  HeaderGroup,
  Row,
  SortingState,
  TableState,
} from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import { CustomColumnDef } from '@experiences/components/table/types';
import {
  DEFAULT_COLUMN_WIDTH_PERCENTAGE,
  ESTIMATED_HEADER_HEIGHT,
  MIN_COLUMN_SIZE,
  MIN_NAME_COLUMN_SIZE,
  NAME_COLUMN_PERCENTAGE,
  ROW_HEIGHT,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/manager-table-constants';
import { DEFAULT_SORTING } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/prospects-manager-prospects-table-columns';
import {
  LAST_ACTIVITY_ALERT_INDICATOR_DAYS,
  PROSPECT_STAGE_FILTER_ORDER,
  PROSPECT_STAGE_TRANSLATION_KEYS,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/constants';
import { LayoutChangeEvent, View } from 'react-native';
import {
  ManagerProspectColumnId,
  ManagerProspectRowData,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';
import { ManagerSectionError } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/manager-section-error';
import { ManagerTablePressableRow } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/manager-table-pressable-row';
import { ManagerTableSkeleton } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/manager-table-skeleton';
import { Table } from '@experiences/components/table/table';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';

const columnHelper = createColumnHelper<ManagerProspectRowData>();

const PROSPECT_METRIC_COLUMN_COUNT = 3;

const PINNED_COLUMNS = { left: [ManagerProspectColumnId.Name] };

const isDormantProspect = (row: ManagerProspectRowData): boolean =>
  (row.daysSinceTouch ?? 0) >= LAST_ACTIVITY_ALERT_INDICATOR_DAYS;

interface ProspectsManagerProspectsTableProps {
  data: ManagerProspectRowData[];
  emptyComponent?: React.ReactNode;
  hasError: boolean;
  isLoading: boolean;
  onRetry: () => void;
  onRowClick: (row: ManagerProspectRowData) => void;
  toolbar?: React.ReactNode;
}

export const ProspectsManagerProspectsTable: React.FC<
  ProspectsManagerProspectsTableProps
> = ({
  data,
  emptyComponent,
  hasError,
  isLoading,
  onRetry,
  onRowClick,
  toolbar,
}) => {
  const [styles, theme] = useStyles();
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  const tableState = useMemo(
    (): Partial<TableState> => ({ sorting }),
    [sorting],
  );

  const handleLayout = (event: LayoutChangeEvent) => {
    setLayoutWidth(event.nativeEvent.layout.width);
  };

  const renderHeaderLabel = useCallback(
    (translationKey: string) => (
      <Typography
        color={theme.sk.color.text.secondary}
        size="$xs"
        variant="label"
      >
        {i18n.t(translationKey)}
      </Typography>
    ),
    [theme],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.name, {
        cell: (info) => {
          const row = info.row.original;

          return (
            <View style={styles.nameCellContainer}>
              <Avatar
                {...createRestaurantAvatarProps(row.name, row.restaurantUUID)}
                size="$sm"
              />
              <Typography
                fontWeight="$medium"
                numberOfLines={1}
                size="$sm"
                textAlign="left"
              >
                {row.name}
              </Typography>
            </View>
          );
        },
        header: () =>
          renderHeaderLabel('sales_portal.prospects.manager.table.prospect'),
        id: ManagerProspectColumnId.Name,
        meta: { initialPercentWidth: NAME_COLUMN_PERCENTAGE },
        minSize: MIN_NAME_COLUMN_SIZE,
        sortDescFirst: false,
        sortingFn: 'text',
      }),
      columnHelper.accessor(
        (row) => PROSPECT_STAGE_FILTER_ORDER.indexOf(row.stage),
        {
          cell: (info) => (
            <Typography
              numberOfLines={1}
              size="$sm"
              textAlign="left"
              variant="body"
            >
              {i18n.t(PROSPECT_STAGE_TRANSLATION_KEYS[info.row.original.stage])}
            </Typography>
          ),
          header: () =>
            renderHeaderLabel('sales_portal.prospects.manager.table.stage'),
          id: ManagerProspectColumnId.Stage,
          meta: { initialPercentWidth: DEFAULT_COLUMN_WIDTH_PERCENTAGE },
          minSize: MIN_COLUMN_SIZE,
          sortDescFirst: false,
        },
      ),
      columnHelper.accessor((row) => row.weeklyValueInMicros, {
        cell: (info) => (
          <Typography
            numberOfLines={1}
            size="$sm"
            textAlign="left"
            variant="body"
          >
            {info.row.original.weeklyValueDisplay}
          </Typography>
        ),
        header: () =>
          renderHeaderLabel(
            'sales_portal.prospects.manager.table.weekly_value',
          ),
        id: ManagerProspectColumnId.Value,
        meta: { initialPercentWidth: DEFAULT_COLUMN_WIDTH_PERCENTAGE },
        minSize: MIN_COLUMN_SIZE,
        sortDescFirst: false,
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => row.daysSinceTouch, {
        cell: (info) => {
          const row = info.row.original;

          return (
            <XStack alignItems="center" gap="$4">
              {isDormantProspect(row) ? (
                <View
                  accessibilityLabel={i18n.t(
                    'sales_portal.prospects.manager.quiet_alert',
                    { count: row.daysSinceTouch },
                  )}
                  accessibilityRole="image"
                >
                  <Icons.AlertCircle color="$iconNegativePrimary" size="$16" />
                </View>
              ) : null}
              <Typography
                color={
                  isDormantProspect(row)
                    ? theme.sk.color.text.error
                    : theme.sk.color.text.primary
                }
                numberOfLines={1}
                size="$sm"
                textAlign="left"
                variant="body"
              >
                {row.daysSinceTouchDisplay}
              </Typography>
            </XStack>
          );
        },
        header: () =>
          renderHeaderLabel('sales_portal.prospects.table.last_activity'),
        id: ManagerProspectColumnId.LastActivity,
        meta: { initialPercentWidth: DEFAULT_COLUMN_WIDTH_PERCENTAGE },
        minSize: MIN_COLUMN_SIZE,
        sortDescFirst: false,
        sortUndefined: 'last',
      }),
    ],
    [renderHeaderLabel, styles, theme],
  );

  const renderHeaderRow = ({
    headerGroup,
  }: {
    headerGroup: HeaderGroup<ManagerProspectRowData>;
  }) => (
    <TableHeaderRow key={headerGroup.id}>
      {headerGroup.headers.map((header) => (
        <TableHeaderCell
          columnId={header.id}
          key={header.id}
          onPress={
            header.column.getCanSort()
              ? () => header.column.toggleSorting()
              : undefined
          }
          pinnedOffset={
            header.column.getIsPinned()
              ? header.column.getStart('left')
              : undefined
          }
          sortDirection={header.column.getIsSorted() || undefined}
        >
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </TableHeaderCell>
      ))}
    </TableHeaderRow>
  );

  const renderRow = useCallback(
    ({ index, row }: { index: number; row: Row<ManagerProspectRowData> }) => (
      <ManagerTablePressableRow
        accessibilityLabel={row.original.name}
        index={index}
        key={row.id}
        onRowClick={onRowClick}
        row={row}
      />
    ),
    [onRowClick],
  );

  const renderCardBody = () => {
    if (isLoading && data.length === 0) {
      return (
        <ManagerTableSkeleton
          columnCount={PROSPECT_METRIC_COLUMN_COUNT}
          columnPercentage={DEFAULT_COLUMN_WIDTH_PERCENTAGE}
          minColumnWidth={MIN_COLUMN_SIZE}
        />
      );
    }

    if (hasError && data.length === 0) {
      return <ManagerSectionError onRetry={onRetry} />;
    }

    if (!layoutWidth) {
      return null;
    }

    return (
      <Table
        cellPadding={theme.sk.spacing.spaceMedium}
        columns={columns as CustomColumnDef<ManagerProspectRowData>[]}
        data={data}
        emptyComponent={emptyComponent}
        estimatedHeaderHeight={ESTIMATED_HEADER_HEIGHT}
        estimatedRowHeight={ROW_HEIGHT}
        gutters={0}
        headerPadding={theme.sk.spacing.spaceMedium}
        initialState={{ columnPinning: PINNED_COLUMNS }}
        isActive
        layoutWidth={layoutWidth}
        onSortingChange={setSorting}
        renderHeaderRow={renderHeaderRow}
        renderRow={renderRow}
        state={tableState}
      />
    );
  };

  return (
    <Surface flex={1}>
      <YStack flex={1} gap="$12" minHeight={0}>
        <XStack
          alignItems="center"
          gap="$8"
          minHeight={CARD_HEAD_MIN_HEIGHT}
          paddingHorizontal="$16"
          paddingTop="$8"
        >
          <Typography
            color="$textPrimary"
            flex={1}
            size="$md"
            variant="heading"
          >
            {i18n.t('sales_portal.prospects.manager.prospects_heading')}
          </Typography>
          {toolbar ? <View style={styles.searchSlot}>{toolbar}</View> : null}
        </XStack>

        <View onLayout={handleLayout} style={styles.tableWrapper}>
          {renderCardBody()}
        </View>
      </YStack>
    </Surface>
  );
};

const CARD_HEAD_MIN_HEIGHT = 48;
const SEARCH_WIDTH = 200;

const useStyles = createStyles((theme) => ({
  nameCellContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
  },
  searchSlot: {
    flexShrink: 0,
    width: SEARCH_WIDTH,
  },
  tableWrapper: {
    flex: 1,
  },
}));
